// Starter emails, one per look. Rebuilt as blocks from the five November drafts.
// Prices and offers are samples; Jen replaces them.

import { BOOKING_URL, JEN_PHOTO, newId, stock, type Block } from './blocks';
import type { LookId } from './types';

export interface Starter {
  look: LookId;
  name: string;
  subject: string;
  preheader: string;
  blocks: Block[];
}

const SITE = 'https://www.lavirmedicalspa.com';
const SKINVIVE = 'https://www.lavirmedicalspa.com/skinvive-by-juvederm';
const GIFT = 'https://www.lavirmedicalspa.com';

const timelineRows = [
  { label: '4 weeks out', heading: 'Dermal filler', text: 'Swelling settles in about two weeks. That leaves time for a touch-up.' },
  { label: '4 weeks out', heading: 'SKINVIVE', text: 'A 15-minute treatment for smoother, glowier cheeks.' },
  { label: '2 weeks out', heading: 'Botox or Jeuveau', text: 'Full results take about 14 days.' },
  { label: '1 week out', heading: 'VI Peel', text: 'Peeling is done in about a week. Fresh skin after.' },
  { label: '2-3 days out', heading: 'HydraFacial or Diamond Glow', text: 'No downtime. The glow is best in the first few days.' },
];

function b<T extends Block['type']>(block: Omit<Extract<Block, { type: T }>, 'id'> & { type: T }): Block {
  return { id: newId(), ...block } as Block;
}

export function starter(look: LookId): Starter {
  switch (look) {
    case 'countdown':
      return {
        look,
        name: 'Holiday timing',
        subject: "Holiday party? Here's when to book.",
        preheader: 'Some treatments need two weeks to settle.',
        blocks: [
          b({ type: 'hero', image: stock('u-glow-4-w.jpg', 'Woman with glowing skin'), headline: "Holiday party in six weeks? Here's when to book.", text: 'Some treatments look best right away. Others need a few weeks to settle. Count back from your date and book in this order.', overlay: false }),
          b({ type: 'timeline', title: 'Count back from your party', rows: [...timelineRows, { label: 'Party night', heading: 'You, rested', text: 'Nobody needs to know why.' }] }),
          b({ type: 'button', label: 'Book my timeline', url: BOOKING_URL, note: '' }),
          b({ type: 'split', image: stock('u-serum-2-t.jpg', 'Skin care serum', 800, 1000), kicker: 'Treatment of the month', heading: 'SKINVIVE by Juvéderm', html: 'Tiny injections of hyaluronic acid smooth the skin on your cheeks. Results can last up to 6 months.', linkLabel: 'Learn about SKINVIVE', url: SKINVIVE, imageSide: 'left' }),
          b({ type: 'offer', heading: 'Give a glow', text: 'Buy a $200 gift card and get a $25 bonus card for yourself. Through December 24.', label: 'Buy a gift card', url: GIFT, style: 'dark' }),
          b({ type: 'quote', photo: JEN_PHOTO, question: '"Will I look frozen?"', answer: 'No. I dose so your face still moves. You should look like you, just less tired. That is the whole goal.', name: 'Jen Tancinco, RN, BSN, Nurse Injector' }),
        ],
      };
    case 'journal':
      return {
        look,
        name: 'LaVir Notes',
        subject: 'The six weeks before the holidays',
        preheader: 'What to book, and when, so it looks its best on the day.',
        blocks: [
          b({ type: 'image', image: stock('u-glow-2-w.jpg', 'Woman with glowing skin'), link: '', caption: '' }),
          b({ type: 'heading', kicker: '', text: 'The six weeks before the holidays', size: 'l' }),
          b({ type: 'text', html: "Every December, someone books Botox two days before a party and is surprised it hasn't kicked in yet. This month is about timing. Some treatments show results the same day. Others need two weeks or more.<br><br>Below is the order I'd book in. If you aren't sure what fits, reply to this email. I'm happy to help you plan.<br><br><b>Jen Tancinco, RN, BSN</b>" }),
          b({ type: 'timeline', title: 'When to book', rows: timelineRows.map((r) => ({ ...r, label: r.label.replace(' out', '') })) }),
          b({ type: 'quote', photo: null, question: '"Will I look frozen?"', answer: "No. I dose so your face still moves. You should look like you, just less tired. Most people won't know what changed. They'll just say you look rested.", name: 'Ask Jen. Reply to this email with your question for next month.' }),
          b({ type: 'split', image: stock('u-serum-1-t.jpg', 'Skin care serum', 800, 1000), kicker: '', heading: 'SKINVIVE by Juvéderm', html: 'Small injections of hyaluronic acid under the skin of the cheeks. It improves smoothness, not volume. The appointment takes about 15 minutes. Results can last up to 6 months.', linkLabel: 'Read more', url: SKINVIVE, imageSide: 'left' }),
          b({ type: 'offer', heading: 'Give a glow this year', text: 'Buy a $200 gift card and get a $25 bonus card for yourself. Through December 24.', label: 'Buy a gift card', url: GIFT, style: 'dark' }),
          b({ type: 'button', label: 'Book an appointment', url: BOOKING_URL, note: '' }),
        ],
      };
    case 'afterdark':
      return {
        look,
        name: 'Holiday gift cards',
        subject: 'Holiday gift cards are here',
        preheader: 'Buy $200, get a $25 bonus card for you.',
        blocks: [
          b({ type: 'hero', image: stock('u-champ-2-w.jpg', 'Champagne toast'), headline: 'Holiday gift cards are here', text: 'For the person who has everything except an hour to themselves.', overlay: true }),
          b({ type: 'offer', heading: 'Buy $200, get $25 for you', text: 'Gift cards work for any treatment, from HydraFacials to Botox. Bonus card through December 24.', label: 'Buy a gift card', url: GIFT, style: 'dark' }),
          b({ type: 'divider' }),
          b({ type: 'timeline', title: 'Book your own before the rush', rows: timelineRows.filter((r) => r.heading !== 'SKINVIVE').map((r) => ({ label: r.label.replace(' out', ' before'), heading: r.heading, text: '' })) }),
          b({ type: 'quote', photo: JEN_PHOTO, question: '"Will I look frozen?"', answer: 'No. I dose so your face still moves. You should look like you, just less tired.', name: 'Jen' }),
          b({ type: 'button', label: 'Book an appointment', url: BOOKING_URL, note: '' }),
        ],
      };
    case 'menu':
      return {
        look,
        name: 'November menu',
        subject: "November menu: this month's prices",
        preheader: 'Botox $11 a unit through November 30.',
        blocks: [
          b({ type: 'heading', kicker: '', text: 'November menu', size: 'l' }),
          b({ type: 'text', html: "This month's prices. Book by November 30." }),
          b({
            type: 'prices',
            title: '',
            groups: [
              { name: 'Injectables', items: [
                { name: 'Botox', was: '$13', price: '$11', unit: '/unit' },
                { name: 'Jeuveau', was: '$12', price: '$10', unit: '/unit' },
                { name: 'Lip filler, 1 syringe', was: '', price: '$550', unit: '' },
                { name: 'SKINVIVE', was: '$650', price: '$575', unit: '' },
              ] },
              { name: 'Facials', items: [
                { name: 'HydraFacial', was: '', price: '$175', unit: '' },
                { name: 'Diamond Glow', was: '', price: '$150', unit: '' },
                { name: 'VI Peel', was: '', price: '$250', unit: '' },
              ] },
              { name: 'Wellness', items: [
                { name: 'IV hydration drip', was: '', price: '$135', unit: '' },
                { name: 'B12 shot', was: '', price: '$25', unit: '' },
              ] },
            ],
            note: 'Botox and Jeuveau take about 14 days for full results. Book at least two weeks before an event.',
          }),
          b({ type: 'button', label: 'Book at this price', url: BOOKING_URL, note: 'Or call (224) 908-6294' }),
          b({ type: 'tiles', items: [
            { image: stock('botox-t.jpg', 'Botox treatment', 800, 1000), label: 'Injectables', url: SITE },
            { image: stock('u-facial-1-t.jpg', 'Facial treatment', 800, 1000), label: 'Facials', url: SITE },
          ] }),
          b({ type: 'offer', heading: 'Gift cards', text: 'Buy $200, get a $25 bonus card for yourself. Through Dec 24.', label: 'Buy a gift card', url: GIFT, style: 'card' }),
          b({ type: 'quote', photo: JEN_PHOTO, question: 'Not sure what is right for you?', answer: 'Reply to this email. I read every one.', name: 'Jen Tancinco, RN' }),
        ],
      };
    case 'onething':
      return {
        look,
        name: 'Glow by the holidays',
        subject: 'Glow by the holidays',
        preheader: 'If you have a party in December, book in November.',
        blocks: [
          b({ type: 'hero', image: stock('u-glow-3-w.jpg', 'Woman with glowing skin'), headline: 'Glow by the holidays.', text: 'Most treatments need time to settle. If you have a party in December, book in November.', overlay: true }),
          b({ type: 'timeline', title: '', rows: timelineRows.filter((r) => r.heading !== 'SKINVIVE').map((r) => ({ label: r.label.replace(' out', ' before'), heading: r.heading.replace(' or ', ', '), text: '' })) }),
          b({ type: 'button', label: 'Book now', url: BOOKING_URL, note: '' }),
          b({ type: 'tiles', items: [
            { image: stock('u-gift-2-t.jpg', 'Wrapped gift', 800, 1000), label: 'Gift cards: buy $200, get $25', url: GIFT },
            { image: stock('u-facial-2-t.jpg', 'Facial treatment', 800, 1000), label: 'SKINVIVE: smoother cheeks', url: SKINVIVE },
          ] }),
        ],
      };
  }
}

/** A blank email in a look: header, one headline, one text, one button. */
export function blank(look: LookId): Starter {
  return {
    look,
    name: 'New email',
    subject: '',
    preheader: '',
    blocks: [
      b({ type: 'heading', kicker: '', text: 'Your headline', size: 'l' }),
      b({ type: 'text', html: 'Hi {{first_name}},<br><br>Write your message here.' }),
      b({ type: 'button', label: 'Book now', url: BOOKING_URL, note: '' }),
    ],
  };
}
